// API client — Phase B Hafta 3 skeleton
// Talks to growtify-env-backend Worker (REST endpoints from Phase B Hafta 1+2).

const BASE = import.meta.env.VITE_API_BASE || "/api";

export async function api<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!r.ok) throw new Error(`API ${r.status}: ${path}`);
  return r.json() as Promise<T>;
}

// Type definitions for the API surface (Phase B Hafta 2)

export interface GovernanceSummary {
  rules: number;
  agents: number;
  skills: number;
  rank_distribution: Array<{ authority_rank: number; n: number }>;
}

export interface TenantsSummary {
  by_type: Array<{ type: string; n: number; mrr_total: number }>;
  by_tier: Array<{ service_tier: string; n: number }>;
  by_status: Array<{ status: string; n: number }>;
  active_total: { total: number; mrr_total_usd: number };
}

export interface Rule {
  id: string;
  name: string;
  scope: string;
  version: number;
  body_md?: string;
  created_at: string;
  updated_at: string;
}

export interface Agent {
  id: string;
  name: string;
  authority_rank: number;
  scope_set: string;
  model_default: string;
  body_md?: string;
}

export interface Skill {
  id: string;
  name: string;
  owning_agent: string | null;
  invoke_pattern: string;
  body_md?: string;
}

export interface WorkItem {
  id: string;
  tenant_id: string;
  source_layer: string;
  status: string;
  priority: string;
  title: string;
  customer_summary: string | null;
  owner_department: string | null;
  created_at: string;
  deadline: string | null;
}

export interface CrossDeptRequest {
  id: string;
  tenant_id: string;
  requester_department: string;
  assignee_department: string;
  status: string;
  priority: string;
  tier: number;
  title: string;
  routing_route: string | null;
  approval_status: string | null;
  created_at: string;
}

export interface TodaySummary {
  window_hours: number;
  tenant_filter: string;
  audit_event_counts: Array<{ event_type: string; n: number }>;
  new_cross_dept_requests: Array<{
    id: string;
    tenant_id: string;
    status: string;
    priority: string;
    title: string;
    requester_department: string;
    assignee_department: string;
    created_at: string;
  }>;
  new_work_items: Array<{
    id: string;
    tenant_id: string;
    status: string;
    priority: string;
    title: string;
    owner_department: string | null;
    source_layer: string;
    created_at: string;
  }>;
  recent_state_changes: Array<{
    resource_type: string;
    resource_id: string;
    tenant_id: string;
    before_state: string | null;
    after_state: string | null;
    occurred_at: string;
  }>;
  pending_approvals: Array<{
    id: string;
    tenant_id: string;
    content_type: string;
    tier: number;
    title: string;
    department: string;
    priority: string;
    due_date: string | null;
  }>;
  briefs_published: Array<{
    id: string;
    brief_type: string;
    title: string;
    health_indicator: string | null;
    health_score: number | null;
    cadence_period: string | null;
    created_at: string;
  }>;
  cron_fires: Array<{
    skill_name: string;
    after_state: string | null;
    occurred_at: string;
  }>;
}
